import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactElement } from 'react'
import { Link, useParams } from 'react-router'

import {
  parentReadDeviceDetailOptions,
  parentReadDeviceScreenshotsOptions,
} from '@/client/@tanstack/react-query.gen'
import { DeviceOnlineIndicator } from '@/components/features/device-online-indicator'
import { ParentRules } from '@/components/features/parent-rules'
import { ParentScreenshotGallery } from '@/components/features/parent-screenshot-gallery'
import { useParentScreenshotViewer } from '@/components/features/use-parent-screenshot-viewer'
import { MissingScreenshot } from '@/components/features/recent-screenshots'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCompactRelativeTime } from '@/lib/relative-time'
import { PARENT_POLL_INTERVAL_MS, parentQueryDefaults } from '@/lib/parent-query'
import { cn } from '@/lib/utils'

const RECENT_LIMIT = 5

export function DeviceScreen(): ReactElement {
  const { deviceId } = useParams()
  const device = useQuery({
    ...parentReadDeviceDetailOptions({ path: { device_id: deviceId ?? '' } }),
    ...parentQueryDefaults,
  })

  if (device.isPending) {
    return (
      <div role="status" aria-label="Loading device" className="space-y-4">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-4 w-36" />
        <Skeleton className="aspect-[16/10] w-full" />
      </div>
    )
  }

  if (device.isError) {
    const error: unknown = device.error
    const notFound =
      typeof error === 'object' &&
      error !== null &&
      'detail' in error &&
      error.detail === 'Device not found'
    return (
      <div className="space-y-4">
        {notFound ? (
          <h1 className="text-2xl font-bold tracking-tight">Device not found</h1>
        ) : (
          <p role="alert" className="text-destructive text-sm">
            This device could not be loaded.
          </p>
        )}
        <Link to="/" viewTransition className="text-primary underline">
          Back to devices
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{device.data.name}</h1>
        <DeviceOnlineIndicator lastHeartbeatAt={device.data.last_heartbeat_at} />
      </div>
      <RecentScreenshotsSection deviceId={device.data.id} />
      <ParentRules deviceId={device.data.id} />
      <ParentScreenshotGallery deviceId={device.data.id} />
    </div>
  )
}

function RecentScreenshotsSection({ deviceId }: { deviceId: string }) {
  const viewer = useParentScreenshotViewer()
  const screenshots = useQuery({
    ...parentReadDeviceScreenshotsOptions({
      path: { device_id: deviceId },
      query: { limit: RECENT_LIMIT },
    }),
    ...parentQueryDefaults,
    refetchInterval: PARENT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  })
  const [now, setNow] = useState(() => Date.now())
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(() => new Set<string>())

  useEffect(() => {
    const ticker = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(ticker)
  }, [])

  const items = screenshots.data?.items ?? []
  const imageBox = 'aspect-[16/10] w-full rounded-md border'

  return (
    <section aria-labelledby="recent-screenshots-heading" className="space-y-3">
      <h2 id="recent-screenshots-heading" className="text-lg font-semibold">
        Recent screenshots
      </h2>
      {screenshots.isPending ? (
        <div role="status" aria-label="Loading screenshots" className="space-y-3">
          {Array.from({ length: RECENT_LIMIT }, (_, row) => (
            <Skeleton key={row} className={imageBox} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No screenshots yet.</p>
      ) : (
        <ul className="space-y-4">
          {items.map((shot, position) => (
            <li key={shot.id} className="space-y-1">
              <button
                type="button"
                onClick={() => viewer.open(items, position)}
                className="block w-full cursor-pointer"
              >
                {unavailable.has(shot.id) ? (
                  <MissingScreenshot className={cn('text-muted-foreground', imageBox)} />
                ) : (
                  <img
                    src={`/api/screenshots/${shot.id}/image`}
                    alt={`Screenshot from ${formatCompactRelativeTime(shot.captured_at, now)}`}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    onError={() =>
                      setUnavailable((current) =>
                        current.has(shot.id) ? current : new Set(current).add(shot.id),
                      )
                    }
                    className={cn('bg-muted object-contain select-none', imageBox)}
                  />
                )}
                <span className="text-muted-foreground w-full text-center text-sm">
                  {formatCompactRelativeTime(shot.captured_at, now)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {viewer.viewer}
    </section>
  )
}
